import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  GetMemberAndGuarntor,
  RemoveMemberAndGuarntor,
  fetchPaymentAndBidInfo,
  fetchVenmoSuggestedMembers,
  fetchAppfolioSuggestedMembers,
  SaveMemberAndGuarntor,
} from "./DataServices/ProspectiveTenantDataService";
import { getapartmentdetails } from "./DataServices/ApartmentsListingsDataServices";
import { fetchAutomaticResponseHistoryById } from "./DataServices/AutomaticResponsesDataServices";
import { IAutomaticResponseHistory } from "./Models";
// import Loading from "./Loading"; // Removed unused import

const TeamSelector = () => {
  const { id } = useParams();
  const [memberGuarantorInfos, setMemberGuarantorInfos] = useState<
    { id: string; name: string; email: string; isGuarantor: boolean }[]
  >([]);
  const [automaticResponseHistory, setAutomaticResponseHistory] =
    useState<IAutomaticResponseHistory>();
  const [expectedTenants, setExpectedTenants] = useState<number | null>(null);
  // Tenant ceiling derived from the apartment's bedroom count (2 tenants per bedroom).
  const [maxTenants, setMaxTenants] = useState<number | null>(null);
  const [expectedGuarantors, setExpectedGuarantors] = useState<number | null>(
    null,
  );
  const [isPublicLead, setIsPublicLead] = useState(false);
  const [memberInput, setMemberInput] = useState<string>("");
  const [emailInput, setEmailInput] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<string>(""); // Changed from "tenant" to ""
  const [inquiryLeadRole, setInquiryLeadRole] = useState<string>("tenant"); // Role for inquiry lead
  const [loading, setLoading] = useState(false);
  const [toDeleteMember, setToDeleteMember] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("success");
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [openMessageBox, setOpenMessageBox] = useState(false);

  const currentTenantCount = memberGuarantorInfos.filter(
    (m) => !m.isGuarantor,
  ).length;
  const tenantLimitReached =
    maxTenants != null && currentTenantCount >= maxTenants;

  const getInquiryLeadName = () =>
    automaticResponseHistory?.firstName || automaticResponseHistory?.lastName
      ? `${automaticResponseHistory?.firstName || ""} ${automaticResponseHistory?.lastName || ""}`.trim()
      : "Primary Inquirer";

  const getInquiryLeadEmail = () =>
    automaticResponseHistory?.email?.trim() || "";

  const getMemberMatchKey = (name: string, email: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    if (normalizedEmail) {
      return `email:${normalizedEmail}`;
    }

    return `name:${name.trim().toLowerCase()}`;
  };

  // Turns a confidently-matched Venmo suggestion into initial member rows.
  // The prospect's known email is preserved by attaching it to whichever
  // suggested name appears to be them; everyone else starts with a blank email
  // for the tenant to fill in (Venmo memos never contain email addresses).
  const buildVenmoPrefilledMembers = (
    suggestion: { tenants?: string[]; guarantors?: string[] },
    prospectName: string,
    prospectEmail: string,
  ) => {
    const trimmedProspect = (prospectName || "").trim();
    const normalizedProspect = trimmedProspect.toLowerCase();
    let prospectMatched = false;

    const buildEntries = (names: string[] = [], isGuarantor: boolean) =>
      names.map((name) => {
        const normalized = name.trim().toLowerCase();
        const isProspect =
          !isGuarantor &&
          !!normalizedProspect &&
          (normalized === normalizedProspect ||
            normalized.includes(normalizedProspect) ||
            normalizedProspect.includes(normalized));
        if (isProspect) prospectMatched = true;
        return {
          name: name.trim(),
          email: isProspect ? prospectEmail || "" : "",
          isGuarantor,
        };
      });

    const entries = [
      ...buildEntries(suggestion.tenants, false),
      ...buildEntries(suggestion.guarantors, true),
    ];

    if (trimmedProspect && !prospectMatched) {
      entries.unshift({
        name: trimmedProspect,
        email: prospectEmail || "",
        isGuarantor: false,
      });
    }

    return entries.map((entry, idx) => ({ id: (idx + 1).toString(), ...entry }));
  };

  // Turns a confidently-derived AppFolio suggestion into initial member rows.
  // Unlike Venmo, every AppFolio row already carries its own real email
  // (from PublicTenantLead/PublicGroupOccupant), so no prospect-matching
  // heuristic is needed here.
  const buildAppfolioPrefilledMembers = (suggestion: {
    tenants?: { name: string; email: string }[];
    guarantors?: { name: string; email: string }[];
  }) => {
    const entries = [
      ...(suggestion.tenants || []).map((t) => ({
        name: t.name,
        email: t.email || "",
        isGuarantor: false,
      })),
      ...(suggestion.guarantors || []).map((g) => ({
        name: g.name,
        email: g.email || "",
        isGuarantor: true,
      })),
    ];

    return entries.map((entry, idx) => ({ id: (idx + 1).toString(), ...entry }));
  };

  const getNumericCount = (...values: Array<unknown>) => {
    for (const value of values) {
      if (typeof value === "number" && !Number.isNaN(value)) {
        return value;
      }
    }

    return null;
  };

  useEffect(() => {
    if (id) {
      // Try to fetch as ARH first
      fetchAutomaticResponseHistoryById(id)
        .then((data: any) => {
          console.log("automaticResponseHistory", data);
          setAutomaticResponseHistory(data);

          // Tenant bounds are a fixed rule based on the apartment's bedroom count:
          // minimum = number of bedrooms, maximum = 2 tenants per bedroom
          // (e.g. a 1-bedroom expects between 1 and 2 tenants).
          // Guarantor count still comes from PaymentAndBidInfo, when set.
          (async () => {
            try {
              const paymentBidInfo = await fetchPaymentAndBidInfo(
                data?.id || id,
              );
              const paymentBid = paymentBidInfo?.[0];

              const recordGuarantorCount = getNumericCount(
                paymentBid?.guarantors,
                paymentBid?.Guarantors,
                paymentBid?.numOfGuarantors,
                paymentBid?.NumOfGuarantors,
                paymentBid?.expectedGuarantors,
                paymentBid?.ExpectedGuarantors,
              );
              setExpectedGuarantors(recordGuarantorCount ?? 0);

              // Fetch apartment list and match by title to find bedroom count
              const apartments = await getapartmentdetails();
              if (
                apartments &&
                Array.isArray(apartments) &&
                apartments.length > 0
              ) {
                const matchByTitle = apartments.find((apt: any) => {
                  const aptTitle = (apt.title || apt.Title || "")
                    .toString()
                    .trim();
                  const arhtitle = (data.title || data.Title || "")
                    .toString()
                    .trim();
                  return aptTitle && arhtitle && aptTitle === arhtitle;
                });
                const bedrooms =
                  matchByTitle?.numOfBedrooms ??
                  matchByTitle?.NumOfBedrooms ??
                  matchByTitle?.bedrooms ??
                  null;
                if (typeof bedrooms === "number" && bedrooms > 0) {
                  setExpectedTenants(bedrooms);
                  setMaxTenants(bedrooms * 2);
                }
              }
            } catch (e) {
              // ignore - expectedTenants/maxTenants will remain null
              console.debug("Failed to resolve bedroom-based tenant bounds:", e);
            }
          })();

          // Use the resolved ARH ID from the backend to fetch members
          const realArhId = data?.id || id;
          GetMemberAndGuarntor(realArhId)
            .then((memberData) => {
              const normalizedMembers = memberData.map((m: any) => ({
                ...m,
                email: m.email || "",
              }));

              const leadEmail = data?.email?.trim().toLowerCase();

              const applyMembers = (members: any[]) => {
                setMemberGuarantorInfos(members);

                const savedLead = members.find(
                  (m: any) => m.email?.trim().toLowerCase() === leadEmail,
                );

                if (savedLead) {
                  setInquiryLeadRole(
                    savedLead.isGuarantor ? "guarantor" : "tenant",
                  );
                }
              };

              if (normalizedMembers.length > 0) {
                applyMembers(normalizedMembers);
                return;
              }

              // Form is empty — see if AppFolio data (Source 1 lead, or the full
              // Source 2 group derived from it) confidently matches this applicant
              // first, then fall back to a Venmo payment match, and if so pre-fill
              // the form with those names.
              // (Derive the prospect's name/email straight from the freshly
              // fetched `data` rather than the `automaticResponseHistory` state,
              // which may not have committed yet inside this same callback.)
              const prospectName =
                data?.firstName || data?.lastName
                  ? `${data?.firstName || ""} ${data?.lastName || ""}`.trim()
                  : "Primary Inquirer";
              const prospectEmail = data?.email?.trim() || "";

              const tryVenmoSuggestion = () =>
                fetchVenmoSuggestedMembers(realArhId)
                  .then((suggestion: any) => {
                    if (
                      suggestion?.matched &&
                      ((suggestion.tenants && suggestion.tenants.length) ||
                        (suggestion.guarantors && suggestion.guarantors.length))
                    ) {
                      const prefilled = buildVenmoPrefilledMembers(
                        suggestion,
                        prospectName,
                        prospectEmail,
                      );
                      applyMembers(prefilled);
                    } else {
                      applyMembers(normalizedMembers);
                    }
                  })
                  .catch(() => applyMembers(normalizedMembers));

              fetchAppfolioSuggestedMembers(realArhId)
                .then((suggestion: any) => {
                  if (
                    suggestion?.matched &&
                    ((suggestion.tenants && suggestion.tenants.length) ||
                      (suggestion.guarantors && suggestion.guarantors.length))
                  ) {
                    applyMembers(buildAppfolioPrefilledMembers(suggestion));
                  } else {
                    tryVenmoSuggestion();
                  }
                })
                .catch(() => tryVenmoSuggestion());
            })
            .catch((err) => {
              console.error("Failed to fetch members", err);
            });
        })
        .catch(() => {
          // If fails, it's a public lead
          setIsPublicLead(true);
          // Fetch Public Lead details (Name/Email)
          fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/public/tenant-leads/${id}`,
            {
              headers: {
                "X-Api-Key": "TDA-SECRET-KEY-2024", // Example API Key, should ideally come from config
                "Content-Type": "application/json",
              },
            },
          )
            .then((res) => res.json())
            .then((data) => {
              // Handle potential casing differences from API
              const leadName = data.name || data.Name || "";
              const leadEmail = data.email || data.Email || "";
              const leadPhone = data.phoneNo || data.PhoneNo || "";
              const leadApartment =
                data.apartmentName || data.ApartmentName || "";

              setAutomaticResponseHistory({
                firstName: leadName.split(" ")[0] || "Inquiry",
                lastName: leadName.split(" ").slice(1).join(" ") || "Lead",
                email: leadEmail,
                phone: leadPhone,
                title: leadApartment,
                workflowType: data.workflowType || data.WorkflowType,
              } as any);
            })
            .catch((err) => console.error("Error fetching lead info:", err));

          // Fetch members for public lead
          fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/Reminder/GetMemberAndGuarntorByPublicLead/${id}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            },
          )
            .then((res) => res.json())
            .then((data) => {
              setMemberGuarantorInfos(
                data.map((m: any) => ({ ...m, email: m.email || "" })),
              );
            })
            .catch((err) => {
              console.error(err);
            });
        });
    }
  }, [id]);

  const saveMemberGuarantorInfos = async () => {
    setLoading(true);
    try {
      const arhId = (automaticResponseHistory as any)?.id || id;
      if (arhId) {
        const inquiryLeadName = getInquiryLeadName();
        const inquiryLeadEmail = getInquiryLeadEmail();
        const inquiryLeadExists = Boolean(
          automaticResponseHistory || isPublicLead,
        );

        const mergedMembers = [...memberGuarantorInfos];

        // Upsert the inquiry lead instead of inserting a duplicate on every save.
        if (inquiryLeadExists) {
          const inquiryLeadKey = getMemberMatchKey(
            inquiryLeadName,
            inquiryLeadEmail,
          );
          const inquiryLeadIndex = mergedMembers.findIndex((member) => {
            const memberKey = getMemberMatchKey(
              member.name || "",
              member.email || "",
            );
            return memberKey === inquiryLeadKey;
          });

          const inquiryLeadEntry = {
            id: inquiryLeadIndex >= 0 ? mergedMembers[inquiryLeadIndex].id : "",
            name: inquiryLeadName,
            email: inquiryLeadEmail,
            isGuarantor: inquiryLeadRole === "guarantor",
          };

          if (inquiryLeadIndex >= 0) {
            mergedMembers[inquiryLeadIndex] = inquiryLeadEntry;
          } else {
            mergedMembers.unshift(inquiryLeadEntry);
          }
        }

        const dedupedMembers = Array.from(
          new Map(
            mergedMembers.map((member) => [
              getMemberMatchKey(member.name || "", member.email || ""),
              member,
            ]),
          ).values(),
        );

        // Every person on the lease must have an email — this also catches
        // Venmo-prefilled rows whose email was left blank for the tenant to add.
        const memberMissingEmail = dedupedMembers.find(
          (m) => !m.email || m.email.trim().length === 0,
        );
        if (memberMissingEmail) {
          setIsNotificationOpen(true);
          setNotificationMessage(
            `Please add an email address for ${memberMissingEmail.name || "all members"} before saving.`,
          );
          setNotificationType("error");
          setLoading(false);
          setTimeout(() => setIsNotificationOpen(false), 3500);
          return;
        }

        // Validate tenants from PaymentAndBidInfo or bedrooms, and only validate guarantors when provided.
        const tenantCount = dedupedMembers.filter((m) => !m.isGuarantor).length;
        const guarantorCount = dedupedMembers.filter(
          (m) => m.isGuarantor,
        ).length;
        const minTenants = expectedTenants ?? 0; // if unknown, allow save
        const minGuarantors = expectedGuarantors ?? 0; // if unknown, ignore guarantor validation

        if (minTenants > 0 && tenantCount < minTenants) {
          setIsNotificationOpen(true);
          setNotificationMessage(
            `Please add at least ${minTenants} tenant(s) for this apartment.`,
          );
          setNotificationType("error");
          setLoading(false);
          setTimeout(() => setIsNotificationOpen(false), 3500);
          return;
        }

        if (maxTenants != null && tenantCount > maxTenants) {
          setIsNotificationOpen(true);
          setNotificationMessage(
            `Too many tenants — this apartment allows at most ${maxTenants} tenant(s) (2 per bedroom).`,
          );
          setNotificationType("error");
          setLoading(false);
          setTimeout(() => setIsNotificationOpen(false), 3500);
          return;
        }

        if (minGuarantors > 0 && guarantorCount < minGuarantors) {
          setIsNotificationOpen(true);
          setNotificationMessage(
            `Please add at least ${minGuarantors} guarantor(s) for this application.`,
          );
          setNotificationType("error");
          setLoading(false);
          setTimeout(() => setIsNotificationOpen(false), 3500);
          return;
        }

        await SaveMemberAndGuarntor(arhId, dedupedMembers);
      }
      setIsNotificationOpen(true);
      setNotificationMessage("Successfully saved member or Guarantor!!");
      setNotificationType("success");
    } catch (error) {
      setIsNotificationOpen(true);
      setNotificationMessage("Failed to saved member or Guarantor!!");
      setNotificationType("error");
    }
    setTimeout(() => {
      setIsNotificationOpen(false);
    }, 3000);
    setLoading(false);
  };

  const checkDuplicateEntry = (name: any) => {
    return memberGuarantorInfos.some(
      (member) =>
        member.name.toLowerCase().trim() === name.toLowerCase().trim(),
    );
  };

  const addTenantGuarantorInfos = () => {
    const trimmedName = memberInput.trim();

    if (checkDuplicateEntry(trimmedName)) {
      setIsNotificationOpen(true);
      setNotificationMessage("Record already exists!");
      setNotificationType("error");
      setTimeout(() => {
        setIsNotificationOpen(false);
      }, 3000);
      return;
    }

    const isGuarantor = selectedRole === "guarantor";
    setMemberGuarantorInfos([
      ...memberGuarantorInfos,
      {
        id: (memberGuarantorInfos.length + 1).toString(),
        name: trimmedName,
        email: emailInput.trim(),
        isGuarantor: isGuarantor,
      },
    ]);
    setMemberInput("");
    setEmailInput("");
    setSelectedRole("");
  };

  // Lets a card's email be filled in / corrected in place — needed because
  // Venmo-detected names arrive with the email left blank for the tenant to add.
  const updateMemberEmail = (id: string, email: string) => {
    setMemberGuarantorInfos((prev) =>
      prev.map((member) => (member.id === id ? { ...member, email } : member)),
    );
  };

  const removeMemberGuarantorInfos = async (id: any) => {
    try {
      setLoading(true);
      if (id.includes("-")) {
        await RemoveMemberAndGuarntor(id);
      }
      setMemberGuarantorInfos(memberGuarantorInfos.filter((e) => e.id !== id));
      setOpenMessageBox(false);
      setIsNotificationOpen(true);
      setNotificationMessage("Successfully delete the Member or Guarantor!!");
      setNotificationType("success");
      setLoading(false);
    } catch (error) {
      setIsNotificationOpen(true);
      setNotificationMessage("Failed to delete Member or Guarantor!!");
      setNotificationType("error");
      setLoading(false);
    }
    setTimeout(() => {
      setIsNotificationOpen(false);
    }, 3000);
  };

  // const UserIcon = () => (
  //   <svg
  //     className="w-full h-full text-gray-400"
  //     fill="currentColor"
  //     viewBox="0 0 24 24"
  //   >
  //     <path d="M12 12c2.7 0 4.9-2.2 4.9-4.9S14.7 2.2 12 2.2 7.1 4.4 7.1 7.1 9.3 12 12 12zm0 2.2c-3.2 0-9.6 1.6-9.6 4.9v1.6c0 .7.6 1.3 1.3 1.3h16.6c.7 0 1.3-.6 1.3-1.3v-1.6c0-3.3-6.4-4.9-9.6-4.9z" />
  //   </svg>
  // );

  const TrashIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">
            Tenants and Guarantors Information
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Add everyone who will be on the lease — tenants and guarantors.
          </p>
        </div>

        {/* Instructions Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-slate-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Before you start
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>Please add the full legal names of everyone who will be on the lease:</p>
                <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 my-2">
                  <li className="flex items-center space-x-2">
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>
                    <span>All co-applicants or roommates</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>
                    <span>Any guarantors (one or multiple)</span>
                  </li>
                </ul>
                <div className="border-l-2 border-amber-400 bg-amber-50 px-3 py-2 rounded-r text-amber-800 text-sm">
                  Completing this step promptly speeds up your application processing.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add Person */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-5">
            Add a person to the lease
          </h2>

          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={memberInput}
                  onChange={(e) => setMemberInput(e.target.value)}
                  placeholder="Enter full name"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="Enter email address"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                Role
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  disabled={tenantLimitReached}
                  onClick={() => setSelectedRole("tenant")}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg border text-left transition-colors ${
                    selectedRole === "tenant"
                      ? "border-slate-400 bg-slate-50"
                      : "border-gray-200 hover:border-gray-300"
                  } ${tenantLimitReached ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <span className="w-8 h-8 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center text-sm">
                    🏠
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-gray-800">Tenant</span>
                    <span className="block text-xs text-gray-500">Will live in the property</span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedRole("guarantor")}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg border text-left transition-colors ${
                    selectedRole === "guarantor"
                      ? "border-slate-400 bg-slate-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <span className="w-8 h-8 rounded-md bg-purple-50 text-purple-600 flex items-center justify-center text-sm">
                    🛡️
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-gray-800">Guarantor</span>
                    <span className="block text-xs text-gray-500">Financial guarantor</span>
                  </span>
                </button>
              </div>
              {tenantLimitReached && (
                <p className="mt-2 text-xs text-amber-600">
                  Tenant limit reached — this apartment allows at most {maxTenants} tenant
                  {maxTenants === 1 ? "" : "s"} (2 per bedroom). You can still add a guarantor.
                </p>
              )}
            </div>

            <button
              disabled={
                memberInput.trim().length === 0 ||
                emailInput.trim().length === 0 ||
                selectedRole === "" ||
                (selectedRole === "tenant" && tenantLimitReached)
              }
              onClick={addTenantGuarantorInfos}
              className="w-full py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-slate-800 transition-colors"
            >
              Add Person to Lease
            </button>
          </div>
        </div>

        {/* Application Group */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-base font-semibold text-gray-900 flex items-center">
              <span className="inline-flex items-center justify-center w-5 h-5 bg-slate-100 text-slate-700 rounded text-xs font-medium mr-2">
                {memberGuarantorInfos.length +
                  (automaticResponseHistory || isPublicLead ? 1 : 0)}
              </span>
              Application Group
            </h2>
            <div className="flex items-center gap-2">
              {/* {expectedTenants != null && maxTenants != null && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                  Requires {expectedTenants}
                  {maxTenants > expectedTenants ? `–${maxTenants}` : ""} tenant
                  {maxTenants > 1 ? "s" : ""}
                  {expectedGuarantors ? ` and ${expectedGuarantors} guarantor${expectedGuarantors > 1 ? "s" : ""}` : ""}
                </span>
              )} */}
              {isPublicLead && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                  <svg
                    className="w-3 h-3 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Inquiry Verified
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Show Inquirer (Lead) with Role Selection */}
            {(automaticResponseHistory || isPublicLead) && (
              <div className="rounded-lg px-4 py-3 border border-slate-200 bg-slate-50 text-sm flex flex-col relative">
                <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-slate-700 text-white text-[10px] font-medium uppercase tracking-wide rounded">
                  Inquiry Lead
                </span>
                <span className="font-medium text-gray-900 pr-20">
                  {automaticResponseHistory?.firstName ||
                  automaticResponseHistory?.lastName
                    ? `${automaticResponseHistory?.firstName || ""} ${automaticResponseHistory?.lastName || ""}`.trim()
                    : "Primary Inquirer"}
                </span>
                <span className="text-xs text-gray-500 truncate">
                  {automaticResponseHistory?.email ||
                    "Contact Email Not Provided"}
                </span>

                {/* Role Selection for Inquiry Lead */}
                <div className="mt-3 space-y-1.5">
                  <label className="block text-xs font-medium text-gray-600">
                    Role
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setInquiryLeadRole("tenant")}
                      className={`flex-1 text-xs py-1.5 px-2 rounded-md transition-colors border ${inquiryLeadRole === "tenant" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}
                    >
                      Tenant
                    </button>
                    <button
                      type="button"
                      onClick={() => setInquiryLeadRole("guarantor")}
                      className={`flex-1 text-xs py-1.5 px-2 rounded-md transition-colors border ${inquiryLeadRole === "guarantor" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}
                    >
                      Guarantor
                    </button>
                  </div>
                </div>
              </div>
            )}

            {memberGuarantorInfos
              // Filter out the inquiry lead to avoid duplication
              .filter((member) => {
                const inquiryLeadEmail = automaticResponseHistory?.email
                  ?.toLowerCase()
                  .trim();
                return (
                  !inquiryLeadEmail ||
                  member.email?.toLowerCase().trim() !== inquiryLeadEmail
                );
              })
              .map((member) => (
                <div
                  key={member.id}
                  className="bg-white rounded-lg px-4 py-3 border border-gray-200 text-sm flex flex-col hover:border-gray-300 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <span className="font-medium text-gray-800">
                      {member.name}
                    </span>
                    <button
                      onClick={() => {
                        setToDeleteMember(member.id);
                        setOpenMessageBox(true);
                      }}
                      className="text-gray-300 hover:text-red-500 transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                  <input
                    type="email"
                    value={member.email}
                    onChange={(e) => updateMemberEmail(member.id, e.target.value)}
                    placeholder="Add email address"
                    className={`text-xs mt-1 w-full bg-transparent border-b focus:outline-none focus:border-slate-400 transition-colors ${
                      member.email
                        ? "text-gray-500 border-transparent hover:border-gray-200"
                        : "text-amber-700 border-amber-300 placeholder-amber-500"
                    }`}
                  />
                  <span
                    className={`text-xs mt-1.5 rounded px-2 py-0.5 w-fit border ${
                      member.isGuarantor
                        ? "bg-purple-50 text-purple-700 border-purple-200"
                        : "bg-blue-50 text-blue-700 border-blue-200"
                    }`}
                  >
                    {member.isGuarantor ? "Guarantor" : "Tenant"}
                  </span>
                </div>
              ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="mb-8">
          <button
            onClick={saveMemberGuarantorInfos}
            disabled={loading}
            className="w-full sm:w-auto px-10 py-3 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Saving..." : "Save Application"}
          </button>
        </div>

        {/* Legal Notice */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-amber-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.347 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1.5">
                Important Legal Information
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                This application is not legally binding and you can withdraw
                anytime before signing. However, if you proceed, all tenants and
                guarantors will be jointly and severally liable for the entire
                rent amount and must follow all building rules and community
                guidelines.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg px-6 py-4 flex items-center space-x-3 shadow-lg">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600"></div>
            <span className="text-sm text-gray-700 font-medium">Processing…</span>
          </div>
        </div>
      )}

      {/* Notification */}
      {isNotificationOpen && (
        <div className="fixed top-4 right-4 z-50">
          <div
            className={`px-4 py-3 rounded-lg shadow-md border text-sm ${
              notificationType === "success"
                ? "bg-white text-gray-800 border-green-200 border-l-4 border-l-green-500"
                : "bg-white text-gray-800 border-red-200 border-l-4 border-l-red-500"
            }`}
          >
            <div className="flex items-center space-x-2">
              <span className={notificationType === "success" ? "text-green-600" : "text-red-600"}>
                {notificationType === "success" ? "✓" : "✕"}
              </span>
              <span className="font-medium">{notificationMessage}</span>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {openMessageBox && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl">
            <div className="mb-5">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center mb-3">
                <TrashIcon />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                Remove person
              </h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to remove this person from the lease
                application?
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setOpenMessageBox(false)}
                className="flex-1 py-2 px-4 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => removeMemberGuarantorInfos(toDeleteMember)}
                className="flex-1 py-2 px-4 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSelector;

/*
const UserIcon = () => (
  <svg
    className="w-full h-full text-gray-400"
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path d="M12 12c2.7 0 4.9-2.2 4.9-4.9S14.7 2.2 12 2.2 7.1 4.4 7.1 7.1 9.3 12 12 12zm0 2.2c-3.2 0-9.6 1.6-9.6 4.9v1.6c0 .7.6 1.3 1.3 1.3h16.6c.7 0 1.3-.6 1.3-1.3v-1.6c0-3.3-6.4-4.9-9.6-4.9z" />
  </svg>
);
*/
